import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClockButton from "@/components/ClockButton";
import { ChevronRight, MapPin, Calendar, Loader2, ClipboardPlus } from "lucide-react";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CaregiverHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShift, setActiveShift] = useState(null);
  const today = DAY_NAMES[new Date().getDay()];

  const refreshActive = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts/active");
      setActiveShift(data.active);
    } catch {
      setActiveShift(null);
    }
  }, []);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      setLoading(false);
    }).catch(() => setLoading(false));
    refreshActive();
  }, [refreshActive]);

  return (
    <div className="space-y-6" data-testid="caregiver-home">
      <div>
        <div className="label-eyebrow">{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
          Hi {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="mt-1 text-sm text-clinical-subtext">
          Select a client to begin documenting your shift.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading clients…
        </div>
      ) : clients.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <ClipboardPlus className="h-10 w-10 mx-auto text-clinical-subtext" />
          <h3 className="mt-3 font-heading text-lg">No clients assigned yet</h3>
          <p className="mt-1 text-sm text-clinical-subtext">
            Ask your admin to assign clients to you.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="client-list">
          {clients.map((c) => {
            const scheduledToday = c.scheduled_days?.includes(today);
            return (
              <li key={c.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/shift/${c.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/shift/${c.id}`);
                    }
                  }}
                  data-testid={`client-card-${c.id}`}
                  className="group block w-full cursor-pointer text-left card-surface p-4 transition-all duration-200 hover:border-clinical-primary/40 hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-clinical-primary/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary font-heading font-semibold text-lg">
                      {c.name.split(" ").map(w => w[0]).slice(0,2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-base font-medium text-clinical-text truncate">
                          {c.name}
                        </h3>
                        {scheduledToday && (
                          <span className="pill bg-clinical-secondary/20 text-clinical-primary">
                            Today
                          </span>
                        )}
                      </div>
                      {c.address && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-clinical-subtext">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{c.address}</span>
                        </div>
                      )}
                      {c.scheduled_days?.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-clinical-subtext">
                          <Calendar className="h-3 w-3" />
                          <span>{c.scheduled_days.join(" · ")}</span>
                        </div>
                      )}
                      <div
                        className="mt-2.5 flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ClockButton
                          clientId={c.id}
                          active={activeShift}
                          onChange={(next) => {
                            setActiveShift(next);
                            refreshActive();
                          }}
                          variant="compact"
                        />
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-clinical-subtext group-hover:text-clinical-primary transition-colors" />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
